import { userApi } from 'entities/user/api';
import { Interval, SupportedProviders } from '@sr/dto';

export type UserIntegrationSettingsDto = {
  id: number;
  domRuUserEmail: string | null;
};

export type UserTelephonyDto = {
  exists: boolean;
  login: string;
  password: string;
  skipAtsLeads: boolean;
};

export type WazzupPhoneDto = {
  contactLinkId: number | null;
  phoneId: number;
  phone: string;
};

export type UserMailDto = {
  exists: boolean;
  email: string;
  password: string;
  provider: SupportedProviders;
  checkInterval: Interval;
  active: boolean;
  deleted: boolean;
};

export type UserMailSettingsDto = Pick<UserMailDto, 'provider' | 'checkInterval' | 'active'>;

const injectedUserApi = userApi.injectEndpoints({
  endpoints: (builder) => ({
    getDomRuUserEmail: builder.query<UserIntegrationSettingsDto, number>({
      query: (userId: number) => `users/${userId}/domruuseremail`,
    }),
    updateDomRuUserEmail: builder.mutation<UserIntegrationSettingsDto, { userId: number; data: UserIntegrationSettingsDto }>({
      query: ({ userId, data }) => ({
        url: `users/${userId}/domruuseremail`,
        body: data,
        method: 'PUT',
      }),
    }),
    getUserTelephony: builder.query<UserTelephonyDto, number>({
      query: (userId: number) => `users/${userId}/telephony`,
    }),
    updateUserTelephonySettings: builder.mutation<void, { userId: number; skipAtsLeads: boolean }>({
      query: ({ userId, skipAtsLeads }) => ({
        url: `users/${userId}/telephony/settings`,
        body: { skipAtsLeads },
        method: 'PATCH',
      }),
    }),
    createUserTelephony: builder.mutation<UserTelephonyDto, { userId: number; login: string; password: string }>({
      query: ({ userId, ...body }) => ({
        url: `users/${userId}/telephony`,
        body,
        method: 'POST',
      }),
    }),
    deleteUserTelephony: builder.mutation<UserTelephonyDto, number>({
      query: (userId) => ({
        url: `users/${userId}/telephony`,
        method: 'DELETE',
      }),
    }),
    getWazzupPhone: builder.query<WazzupPhoneDto | null, number>({
      query: (userId: number) => `users/${userId}/wazzup-phone`,
    }),
    activateWazzupPhone: builder.mutation<WazzupPhoneDto, { userId: number; contactLinkId: number }>({
      query: ({ userId, contactLinkId }) => ({
        url: `users/${userId}/wazzup-phone`,
        body: { contactLinkId },
        method: 'PUT',
      }),
    }),
    deactivateWazzupPhone: builder.mutation<void, number>({
      query: (userId) => ({
        url: `users/${userId}/wazzup-phone`,
        method: 'DELETE',
      }),
    }),
    getUserMail: builder.query<UserMailDto, number>({
      query: (userId: number) => `users/${userId}/mail`,
    }),
    createUserMail: builder.mutation<UserMailDto, { userId: number; email: string; password: string } & UserMailSettingsDto>({
      query: ({ userId, ...body }) => ({
        url: `users/${userId}/mail`,
        body,
        method: 'POST',
      }),
    }),
    changeUserMailPassword: builder.mutation<UserMailDto, { userId: number; password: string }>({
      query: ({ userId, password }) => ({
        url: `users/${userId}/mail/password`,
        body: { password },
        method: 'PATCH',
      }),
    }),
    updateUserMailSettings: builder.mutation<UserMailDto, { userId: number; settings: UserMailSettingsDto }>({
      query: ({ userId, settings }) => ({
        url: `users/${userId}/mail/settings`,
        body: settings,
        method: 'PATCH',
      }),
    }),
    deleteUserMail: builder.mutation<UserMailDto, number>({
      query: (userId) => ({
        url: `users/${userId}/mail`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const userIntegrationApi = injectedUserApi.enhanceEndpoints({
  addTagTypes: ['user-integration', 'user-telephony', 'wazzup-phone', 'user-mail'],
  endpoints: {
    getDomRuUserEmail: {
      providesTags: (result, error, userId) => (result ? [{ type: 'user-integration', id: userId }] : ['user-integration']),
    },
    updateDomRuUserEmail: {
      invalidatesTags: (result, error, query) => [{ type: 'user-integration', id: query.userId }],
    },
    getUserTelephony: {
      providesTags: (result, error, userId) => [{ type: 'user-telephony', id: userId }],
    },
    updateUserTelephonySettings: {
      invalidatesTags: (result, error, query) => [{ type: 'user-telephony', id: query.userId }],
    },
    createUserTelephony: {
      invalidatesTags: (result, error, query) => [{ type: 'user-telephony', id: query.userId }],
    },
    deleteUserTelephony: {
      invalidatesTags: (result, error, userId) => [{ type: 'user-telephony', id: userId }],
    },
    getWazzupPhone: {
      providesTags: (result, error, userId) => [{ type: 'wazzup-phone', id: userId }],
    },
    activateWazzupPhone: {
      invalidatesTags: (result, error, query) => [{ type: 'wazzup-phone', id: query.userId }],
    },
    deactivateWazzupPhone: {
      invalidatesTags: (result, error, userId) => [{ type: 'wazzup-phone', id: userId }],
    },
    getUserMail: {
      providesTags: (result, error, userId) => [{ type: 'user-mail', id: userId }],
    },
    createUserMail: {
      invalidatesTags: (result, error, query) => [{ type: 'user-mail', id: query.userId }],
    },
    changeUserMailPassword: {
      invalidatesTags: (result, error, query) => [{ type: 'user-mail', id: query.userId }],
    },
    updateUserMailSettings: {
      invalidatesTags: (result, error, query) => [{ type: 'user-mail', id: query.userId }],
    },
    deleteUserMail: {
      invalidatesTags: (result, error, userId) => [{ type: 'user-mail', id: userId }],
    },
  },
});
